import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, AlertTriangle, Briefcase, MapPin, DollarSign, Loader2, ShieldAlert, Clock, ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '../lib/utils';
import SeoHead from '../components/SeoHead';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ClientMissionPage = () => {
  const { token } = useParams();
  const [mission, setMission] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [presentedCount, setPresentedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorType(null);
    try {
      const res = await fetch(`${API_BASE}/client-portal/t/${token}`);
      if (!res.ok) {
        const err = await res.json();
        const detail = err.detail || 'Lien invalide';
        if (res.status === 410) setErrorType(detail.includes('expiré') ? 'expired' : 'revoked');
        else setErrorType('invalid');
        throw new Error(detail);
      }
      const data = await res.json();
      setMission(data.mission);
      setCandidates(data.candidates || []);
      setPresentedCount(data.presented_count || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDecision = async (entryId, decision) => {
    setDecidingId(entryId);
    setFeedback(null);
    try {
      const res = await fetch(`${API_BASE}/client-portal/t/${token}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortlist_entry_id: entryId, decision }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFeedback({ type: 'error', message: data.detail || 'Erreur' });
      } else {
        setFeedback({ type: 'success', message: data.message });
        await fetchData();
      }
    } catch (e) {
      setFeedback({ type: 'error', message: 'Erreur de connexion' });
    } finally {
      setDecidingId(null);
    }
  };

  const presented = candidates.filter(c => c.status === 'presented_to_client');
  const interested = candidates.filter(c => c.status === 'client_interested');
  const interviewDone = candidates.filter(c => c.status === 'interview_done');
  const hired = candidates.filter(c => c.status === 'hired');

  const seoTag = (
    <SeoHead
      pageTitle="Mission client — Talent Partner"
      pageDescription="Espace client privé (accès par lien sécurisé)."
      noindex={true}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="client-mission-loading">
        {seoTag}
        <div className="text-center"><Loader2 className="h-8 w-8 animate-spin text-tp-primary mx-auto mb-3" /><p className="text-sm text-gray-500">Chargement...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="client-mission-error">
        {seoTag}
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center max-w-md w-full">
          {errorType === 'expired' ? (
            <><Clock className="h-10 w-10 text-orange-400 mx-auto mb-3" /><h2 className="text-lg font-semibold text-gray-800 mb-2">Lien expiré</h2></>
          ) : errorType === 'revoked' ? (
            <><ShieldAlert className="h-10 w-10 text-red-400 mx-auto mb-3" /><h2 className="text-lg font-semibold text-gray-800 mb-2">Lien révoqué</h2></>
          ) : (
            <><AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" /><h2 className="text-lg font-semibold text-gray-800 mb-2">Accès refusé</h2></>
          )}
          <p className="text-sm text-gray-500">{error}</p>
          <p className="text-xs text-gray-400 mt-4">Talent Partner Solutions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-mission-page">
      {seoTag}
      {/* Header */}
      <div className="bg-tp-primary text-white px-4 py-6 sm:px-8">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-wider text-white/60 mb-1">Mission</p>
          <h1 className="text-xl sm:text-2xl font-display font-semibold" data-testid="client-mission-title">{mission?.role_required || 'Mission'}</h1>
          {mission?.client_name && <p className="text-sm text-white/70 mt-1">{mission.client_name}</p>}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-8 space-y-6">
        {/* Feedback */}
        {feedback && (
          <div className={cn("rounded-lg p-3 text-sm", feedback.type === 'success' && "bg-green-50 text-green-700 border border-green-200", feedback.type === 'error' && "bg-red-50 text-red-700 border border-red-200")} data-testid="client-feedback">
            {feedback.message}
          </div>
        )}

        {/* Presented candidates — ACTION ZONE */}
        {presented.length > 0 && (
          <section data-testid="client-presented-section">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Candidats proposés ({presented.length})</h2>
            <div className="space-y-3">
              {presented.map(entry => (
                <ClientCandidateCard
                  key={entry.id}
                  entry={entry}
                  onInterested={() => handleDecision(entry.id, 'interested')}
                  onNotInterested={() => handleDecision(entry.id, 'not_interested')}
                  isDeciding={decidingId === entry.id}
                />
              ))}
            </div>
          </section>
        )}

        {/* Interested */}
        {interested.length > 0 && (
          <section data-testid="client-interested-section">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Vous êtes intéressé ({interested.length})</h2>
            <div className="space-y-3">
              {interested.map(entry => (
                <StatusCard key={entry.id} entry={entry} label="Intéressé" cls="border-blue-200" badgeCls="bg-blue-50 text-blue-600" />
              ))}
            </div>
          </section>
        )}

        {/* Interview done */}
        {interviewDone.length > 0 && (
          <section data-testid="client-interview-done-section">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Entretien réalisé ({interviewDone.length})</h2>
            <div className="space-y-3">
              {interviewDone.map(entry => (
                <StatusCard key={entry.id} entry={entry} label="Entretien réalisé" cls="border-indigo-200" badgeCls="bg-indigo-50 text-indigo-600" />
              ))}
            </div>
          </section>
        )}

        {/* Hired */}
        {hired.length > 0 && (
          <section data-testid="client-hired-section">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Recrutés ({hired.length})</h2>
            <div className="space-y-3">
              {hired.map(entry => (
                <StatusCard key={entry.id} entry={entry} label="Recruté" cls="border-green-200" badgeCls="bg-green-50 text-green-600" />
              ))}
            </div>
          </section>
        )}

        {/* Empty */}
        {presented.length === 0 && interested.length === 0 && interviewDone.length === 0 && hired.length === 0 && (
          <div className="text-center py-12" data-testid="client-empty-state">
            <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Aucun candidat proposé pour le moment.</p>
          </div>
        )}

        <div className="text-center pt-6 pb-4"><p className="text-xs text-gray-400">Talent Partner Solutions</p></div>
      </div>
    </div>
  );
};

/* Status card (read-only) */
const StatusCard = ({ entry, label, cls, badgeCls }) => {
  const c = entry.candidate || {};
  return (
    <div className={`bg-white rounded-lg border ${cls} p-4`} data-testid={`client-status-card-${entry.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-tp-primary/10 flex items-center justify-center">
          <span className="text-xs font-semibold text-tp-primary">{(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm truncate">{c.first_name} {c.last_name}</p>
          <p className="text-xs text-gray-500 truncate">{c.main_profession || '-'}</p>
        </div>
        <span className={`text-xs font-medium px-2 py-1 rounded ${badgeCls}`}>{label}</span>
      </div>
      {entry.client_price && (
        <p className="text-xs text-gray-500 mt-2 pl-12">Coût mensuel : <strong>{Math.round(entry.client_price).toLocaleString('fr-FR')}€</strong></p>
      )}
    </div>
  );
};

/* Client Candidate Card (with actions) */
const ClientCandidateCard = ({ entry, onInterested, onNotInterested, isDeciding }) => {
  const c = entry.candidate || {};
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`client-candidate-card-${entry.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-tp-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-tp-primary">{(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{c.first_name} {c.last_name}</h3>
            <p className="text-sm text-gray-500 truncate">{c.main_profession || 'Non défini'}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
          {c.country && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.country}</span>}
          {/* Show client_price, NEVER expected_salary */}
          {entry.client_price && (
            <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{Math.round(entry.client_price).toLocaleString('fr-FR')}€/mois</span>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          <button onClick={onNotInterested} disabled={isDeciding} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors disabled:opacity-50" data-testid={`client-not-interested-btn-${entry.id}`}>
            <ThumbsDown className="h-4 w-4" /> Pas intéressé
          </button>
          <button onClick={onInterested} disabled={isDeciding} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-medium rounded-lg bg-tp-primary text-white hover:bg-tp-primary-light transition-colors disabled:opacity-50" data-testid={`client-interested-btn-${entry.id}`}>
            {isDeciding ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4" />} Intéressé
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientMissionPage;
